import { defineIntegration } from '../integration';
import { addMetadataToStackFrames, stripMetadataFromStackFrames } from '../metadata';
import type { EventItem } from '../types/envelope';
import type { Event } from '../types/event';
import type { StackFrame } from '../types/stackframe';
import { forEachEnvelopeItem } from '../utils/envelope';
import { getFramesFromEvent } from '../utils/stacktrace';
import { GLOBAL_OBJ } from '../utils/worldwide';

interface Options {
  /**
   * Keys that have been provided in the Sentry bundler plugin via the the `applicationKey` option, identifying your bundles.
   *
   * - Webpack plugin: https://www.npmjs.com/package/@sentry/webpack-plugin#applicationkey
   * - Vite plugin: https://www.npmjs.com/package/@sentry/vite-plugin#applicationkey
   * - Esbuild plugin: https://www.npmjs.com/package/@sentry/esbuild-plugin#applicationkey
   * - Rollup plugin: https://www.npmjs.com/package/@sentry/rollup-plugin#applicationkey
   */
  filterKeys: string[];

  /**
   * Defines how the integration should behave. "Third-Party Stack Frames" are stack frames that did not come from files marked with a matching bundle key.
   *
   * You can define the behaviour with one of 4 modes:
   * - `drop-error-if-contains-third-party-frames`: Drop error events that contain at least one third-party stack frame.
   * - `drop-error-if-exclusively-contains-third-party-frames`: Drop error events that exclusively contain third-party stack frames.
   * - `apply-tag-if-contains-third-party-frames`: Keep all error events, but apply a `third_party_code: true` tag in case the error contains at least one third-party stack frame.
   * - `apply-tag-if-exclusively-contains-third-party-frames`: Keep all error events, but apply a `third_party_code: true` tag in case the error contains exclusively third-party stack frames.
   *
   * If you chose the mode to only apply tags, the tags can then be used in Sentry to filter your issue stream by entering `!third_party_code:True` in the search bar.
   */
  behaviour:
    | 'drop-error-if-contains-third-party-frames'
    | 'drop-error-if-exclusively-contains-third-party-frames'
    | 'apply-tag-if-contains-third-party-frames'
    | 'apply-tag-if-exclusively-contains-third-party-frames';

  /**
   * @experimental
   * If set to true, the integration will ignore frames that are internal to the Sentry SDK from the third-party frame detection.
   * Note that enabling this option might lead to errors being misclassified as third-party errors.
   */
  ignoreSentryInternalFrames?: boolean;
}

/**
 * This integration allows you to filter out, or tag error events that do not come from user code marked with a bundle key via the Sentry bundler plugins.
 */
export const thirdPartyErrorFilterIntegration = defineIntegration((options: Options) => {
  return {
    name: 'ThirdPartyErrorsFilter' as const,
    setup(client) {
      // We need to strip metadata from stack frames before sending them to Sentry since these are client side only.
      client.on('beforeEnvelope', envelope => {
        forEachEnvelopeItem(envelope, (item, type) => {
          if (type === 'event') {
            const event = Array.isArray(item) ? (item as EventItem)[1] : undefined;

            if (event) {
              stripMetadataFromStackFrames(event);
              item[1] = event;
            }
          }
        });
      });

      client.on('applyFrameMetadata', event => {
        // Only apply stack frame metadata to error events
        if (event.type) {
          return;
        }

        const stackParser = client.getOptions().stackParser;
        addMetadataToStackFrames(stackParser, event);
      });
    },

    preprocessEvent(event) {
      // Snapshot the depth counter onto the event before any event processors run.
      // This is necessary because async event processors could cause the finally block
      // in sentryWrapped to decrement the counter before processEvent reads it.
      if (options.ignoreSentryInternalFrames && (GLOBAL_OBJ._sentryWrappedDepth ?? 0) > 0) {
        event.sdkProcessingMetadata = {
          ...event.sdkProcessingMetadata,
          insideSentryWrapped: true,
        };
      }
    },

    processEvent(event) {
      const insideSentryWrapped = options.ignoreSentryInternalFrames
        ? event.sdkProcessingMetadata?.insideSentryWrapped === true && event.exception?.values?.length === 1
        : false;
      const frameKeys = getBundleKeysForAllFramesWithFilenames(
        event,
        options.ignoreSentryInternalFrames,
        insideSentryWrapped,
      );

      if (frameKeys) {
        const arrayMethod =
          options.behaviour === 'drop-error-if-contains-third-party-frames' ||
          options.behaviour === 'apply-tag-if-contains-third-party-frames'
            ? 'some'
            : 'every';

        const behaviourApplies = frameKeys[arrayMethod](keys => !keys.some(key => options.filterKeys.includes(key)));

        if (behaviourApplies) {
          const shouldDrop =
            options.behaviour === 'drop-error-if-contains-third-party-frames' ||
            options.behaviour === 'drop-error-if-exclusively-contains-third-party-frames';
          if (shouldDrop) {
            return null;
          } else {
            event.tags = {
              ...event.tags,
              third_party_code: true,
            };
          }
        }
      }

      return event;
    },
  };
});

/** Checks if a frame is Sentry-internal via runtime depth, function name, or source patterns. */
function isSentryInternalFrame(frame: StackFrame, frameIndex: number, insideSentryWrapped: boolean): boolean {
  // Only match the last frame (index 0 in reversed stack)
  if (frameIndex !== 0) {
    return false;
  }

  // When processEvent runs inside a sentryWrapped call and the frame looks minified,
  // the outermost frame is the sentryWrapped function with a mangled name.
  // We gate on minified shape to avoid false positives in non-minified builds
  // where stripSentryFramesAndReverse already removed the sentryWrapped frame.
  if (insideSentryWrapped && isLikelyMinifiedSentryWrappedFrame(frame)) {
    return true;
  }

  // Match by function name (works when function names survive bundling but source patterns don't)
  if (frame.function === 'sentryWrapped') {
    return true;
  }

  if (!frame.context_line || !frame.filename) {
    return false;
  }

  // Match by source code patterns (works in development / unbundled builds)
  if (
    !frame.filename.includes('sentry') ||
    !frame.filename.includes('helpers') || // Filename would look something like this: 'node_modules/@sentry/browser/build/npm/esm/helpers.js'
    !frame.context_line.includes(SENTRY_INTERNAL_FN_APPLY) // Must have context_line with the exact fn.apply pattern
  ) {
    return false;
  }

  // Check pre_context array for comment pattern
  if (frame.pre_context) {
    const len = frame.pre_context.length;
    for (let i = 0; i < len; i++) {
      if (frame.pre_context[i]?.includes(SENTRY_INTERNAL_COMMENT)) {
        return true;
      }
    }
  }

  return false;
}

function getBundleKeysForAllFramesWithFilenames(
  event: Event,
  ignoreSentryInternalFrames?: boolean,
  insideSentryWrapped?: boolean,
): string[][] | undefined {
  const frames = getFramesFromEvent(event);

  if (!frames) {
    return undefined;
  }

  return frames
    .filter((frame, index) => {
      // Exclude frames without a filename
      if (!frame.filename) {
        return false;
      }
      // Exclude frames without location info, since these are likely native code or built-ins.
      // JS frames have lineno/colno, WASM frames have instruction_addr instead.
      if (frame.lineno == null && frame.colno == null && frame.instruction_addr == null) {
        return false;
      }
      // Optionally ignore Sentry internal frames
      return !ignoreSentryInternalFrames || !isSentryInternalFrame(frame, index, !!insideSentryWrapped);
    })
    .map(frame => {
      if (!frame.module_metadata) {
        return [];
      }
      return Object.keys(frame.module_metadata)
        .filter(key => key.startsWith(BUNDLER_PLUGIN_APP_KEY_PREFIX))
        .map(key => key.slice(BUNDLER_PLUGIN_APP_KEY_PREFIX.length));
    });
}

function isLikelyMinifiedSentryWrappedFrame(frame: StackFrame): boolean {
  return !frame.context_line && !frame.pre_context && !!frame.function && frame.function.length <= 2;
}

const BUNDLER_PLUGIN_APP_KEY_PREFIX = '_sentryBundlerPluginAppKey:';
const SENTRY_INTERNAL_COMMENT = 'Attempt to invoke user-land function';
const SENTRY_INTERNAL_FN_APPLY = 'fn.apply(this, wrappedArguments)';



// WEBPACK FOOTER //
// ../../../src/integrations/third-party-errors-filter.ts